import React, { Suspense, lazy } from 'react';
import { Toaster } from 'sonner';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MetarProvider } from './contexts/MetarContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AnalyticsProvider } from './components/analytics/AnalyticsProvider';
import { AppBootstrap } from './components/AppBootstrap';
import Layout from './components/Layout';
import { OnboardingModal } from './components/OnboardingModal';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageLoader } from './components/ui/PageLoader';

// Lazy-loaded route components. Named exports are re-exported as `default` so React.lazy can consume them.
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AircraftsPage = lazy(() => import('./pages/AircraftsPage').then(m => ({ default: m.AircraftsPage })));
const AircraftDetailsPage = lazy(() => import('./pages/AircraftDetailsPage'));
const BookingsPage = lazy(() => import('./pages/BookingsPage').then(m => ({ default: m.BookingsPage })));
const MembersPage = lazy(() => import('./pages/MembersPage').then(m => ({ default: m.MembersPage })));
const MemberDetails = lazy(() => import('./pages/MemberDetails'));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const StudentAssignmentsPage = lazy(() => import('./pages/StudentAssignmentsPage').then(m => ({ default: m.StudentAssignmentsPage })));
const StudentAssignmentDetailsPage = lazy(() => import('./pages/StudentAssignmentDetailsPage').then(m => ({ default: m.StudentAssignmentDetailsPage })));
const TrainingProgressPage = lazy(() => import('./pages/TrainingProgressPage').then(m => ({ default: m.TrainingProgressPage })));
const DispatchCenterPage = lazy(() => import('./pages/DispatchCenterPage').then(m => ({ default: m.DispatchCenterPage })));
const AvailabilityPage = lazy(() => import('./pages/AvailabilityPage').then(m => ({ default: m.AvailabilityPage })));
const MaintenanceReportPage = lazy(() => import('./pages/MaintenanceReportPage').then(m => ({ default: m.MaintenanceReportPage })));
const MaintenanceReportsPage = lazy(() => import('./pages/MaintenanceReportsPage').then(m => ({ default: m.MaintenanceReportsPage })));
const MaintenanceReportDetailsPage = lazy(() => import('./pages/MaintenanceReportDetailsPage'));
const AdminMaintenanceReportsPage = lazy(() => import('./pages/AdminMaintenanceReportsPage').then(m => ({ default: m.AdminMaintenanceReportsPage })));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const InsightsDashboardPage = lazy(() => import('./pages/InsightsDashboardPage').then(m => ({ default: m.InsightsDashboardPage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage').then(m => ({ default: m.AcceptInvitationPage })));
const MembershipRequestStatusPage = lazy(() => import('./pages/MembershipRequestStatusPage').then(m => ({ default: m.MembershipRequestStatusPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { isOnboardingComplete, loading: onboardingLoading } = useOnboarding();

  if (authLoading || onboardingLoading) {
    return null;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isOnboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

function AppWithDebugLog() {
  return (
    <Suspense fallback={<PageLoader fullPage size="lg" />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/membership-request/:requestId" element={<MembershipRequestStatusPage />} />
        <Route path="/onboarding" element={<OnboardingModal />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Settings - All authenticated users (org editing is gated by isAdmin inside the page) */}
          <Route path="settings" element={<SettingsPage />} />

          {/* Members - roles with MEMBERS:READ */}
          <Route path="members" element={<ProtectedRoute requiredPermission="MEMBERS:READ"><MembersPage /></ProtectedRoute>} />
          <Route path="members/:id" element={<MemberDetails />} />

          {/* Aircraft - roles with AIRCRAFT:READ */}
          <Route path="aircrafts" element={<ProtectedRoute requiredPermission="AIRCRAFT:READ"><AircraftsPage /></ProtectedRoute>} />
          <Route path="aircrafts/:id" element={<AircraftDetailsPage />} />

          {/* Bookings - All authenticated users */}
          <Route path="bookings" element={<BookingsPage />} />
          {/* HIDDEN: Flight Logs - keeping route for future use */}
          {/* <Route path="flight-logs" element={<FlightLogPage />} /> */}

          {/* Maintenance - all with MAINTENANCE:READ; admin console gated by ORG:MANAGE_SETTINGS */}
          <Route path="maintenance/report" element={<MaintenanceReportPage />} />
          <Route path="maintenance/reports" element={<MaintenanceReportsPage />} />
          <Route path="maintenance/reports/:id" element={<MaintenanceReportDetailsPage />} />
          <Route path="admin/maintenance-reports" element={<ProtectedRoute requiredPermission="ORG:MANAGE_SETTINGS"><AdminMaintenanceReportsPage /></ProtectedRoute>} />

          {/* Admin console - ORG:MANAGE_SETTINGS for audit; ORG:VIEW_ANALYTICS for reports/insights */}
          <Route path="admin/audit-log" element={<ProtectedRoute requiredPermission="ORG:MANAGE_SETTINGS"><AuditLogPage /></ProtectedRoute>} />
          <Route path="admin/reports" element={<ProtectedRoute requiredPermission="ORG:VIEW_ANALYTICS"><ReportsPage /></ProtectedRoute>} />
          <Route path="admin/insights" element={<ProtectedRoute requiredPermission="ORG:VIEW_ANALYTICS"><InsightsDashboardPage /></ProtectedRoute>} />

          {/* Flight School Routes */}
          <Route path="availability" element={<ProtectedRoute anyPermission={['AVAILABILITY:READ_OWN', 'AVAILABILITY:READ_ALL']}><AvailabilityPage /></ProtectedRoute>} />
          {/* HIDDEN: Flight Planning - keeping route for future use but not currently accessible via navigation */}
          {/* <Route path="flight-planning" element={<FlightPlanningPage />} /> */}
          <Route path="assignments" element={<ProtectedRoute requiredPermission="ASSIGNMENTS:READ"><StudentAssignmentsPage /></ProtectedRoute>} />
          <Route path="assignments/:studentId" element={<ProtectedRoute requiredPermission="ASSIGNMENTS:READ"><StudentAssignmentDetailsPage /></ProtectedRoute>} />
          <Route path="training" element={<ProtectedRoute requiredPermission="TRAINING:READ"><TrainingProgressPage /></ProtectedRoute>} />
          <Route path="dispatch" element={<ProtectedRoute requiredPermission="BOOKINGS:DISPATCH_OVERRIDE"><DispatchCenterPage /></ProtectedRoute>} />

          {/* Catch-all for unknown URLs. Nested inside the authenticated layout so:
              - authenticated users see the 404 page (with nav chrome)
              - unauthenticated users are redirected to /login by PrivateRoute above
              The parent path="/" prefix-matches every URL, so this is the single
              fallback for the whole app. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <OrganizationProvider>
                <AnalyticsProvider>
                <AppBootstrap>
                  <NotificationProvider>
                    <OnboardingProvider>
                      <MetarProvider>
                        <ErrorBoundary>
                          <AppWithDebugLog />
                        </ErrorBoundary>
                        <Toaster position="top-center" richColors closeButton />
                      </MetarProvider>
                    </OnboardingProvider>
                  </NotificationProvider>
                </AppBootstrap>
                </AnalyticsProvider>
              </OrganizationProvider>
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
